import React, { useContext, useEffect, useState, useCallback } from "react";
import { AgGridReact } from "@ag-grid-community/react";
import { AllModules, ColDef, RowClickedEvent } from "@ag-grid-enterprise/all-modules";
import { NonIdealState } from "@blueprintjs/core";

import {
    SearchContext,
    TemplateGridSelectContext,
    ShowDeleteAlertContext,
    DarkThemeContext
} from "../../utils/context";
import { useTemplateGridOptions } from "../../hooks/useTemplateGridOptions";
import { useQuery } from "@apollo/client";
import { GET_AVAILABLE_TEMPLATES } from "src/operations/queries/getAvailableTemplates";
import { GetAvailableTemplates } from "src/operations/queries/__generated__/GetAvailableTemplates";

import CreateInstructionRunDiaglog from "../runs/CreateTemplateRunDialog";
import { AvailableTemplate } from "src/models/AvailableTemplates";

import ".././TemplateGrid.scss";

export default function TemplateGrid() {
    const dark = useContext(DarkThemeContext);
    const [search] = useContext(SearchContext);
    const [gridOptions, gridApi] = useTemplateGridOptions(columnDefs);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const { loading, data, error } = useQuery<GetAvailableTemplates>(GET_AVAILABLE_TEMPLATES, {
        fetchPolicy: "cache-and-network",
        errorPolicy: "all"
        // pollInterval: 3000
    });

    const [selectedTemplate, setSelectedTemplate] = useState<undefined | AvailableTemplate>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const onRowClicked = useCallback((e: RowClickedEvent) => {
        const data = e.data;
        setSelectedTemplate(data ? data : undefined);
        setDialogOpen(true);
    }, []);
    useEffect(() => {
        if (gridOptions) {
            const getContextMenuItems = (params: any) => {
                return [
                    "copy",
                    "copyWithHeaders",
                    // "paste"
                    "separator",
                    {
                        name: "Edit",
                        action: () => {
                            console.log("Edit Clicked");
                        },
                        icon: "<span class='ag-icon ag-icon-menu'></span>"
                    }
                    // {
                    //     name: "Delete",
                    //     action: () => {
                    //         const node: AvailableTemplate = params.node.data;
                    //         setSelectedTemplate(node);
                    //         setConfirmOpen(true);
                    //     },
                    //     icon: "<span class='ag-icon ag-icon-cross'></span>"
                    // }
                ];
            };
            gridOptions.getContextMenuItems = getContextMenuItems;
        }
    }, [gridOptions]);

    useEffect(() => {
        if (gridApi) {
            if (data) {
                gridApi.setRowData(reshape(data));
            }
        }
    }, [gridApi, data]);

    useEffect(() => {
        if (gridApi) {
            gridApi.setQuickFilter(search);
        }
    }, [gridApi, search]);

    useEffect(() => {
        if (gridApi) {
            if (loading) {
                gridApi.showLoadingOverlay();
            } else {
                gridApi.hideOverlay();
            }
        }
    }, [gridApi, loading]);

    // code to delete templates
    // const { mutate } = useDeleteDraft();
    // const handleDelete = () => {
    //     setConfirmOpen(false);
    //     mutate({ variables: { input: { companySysId: selectedTemplate?.companySysId, processId: selectedTemplate?.processId } } })
    //         .then(res => {
    //             if (res.errors) {
    //                 DeletionToaster.show({ message: `Failed To Delete Template: ${selectedTemplate?.name}`, intent: Intent.DANGER });
    //                 return;
    //             }
    //             DeletionToaster.show({ message: `Deleted Template: ${selectedTemplate?.name}`, intent: Intent.WARNING });
    //         })
    //         .catch(err => {
    //             console.log("catch error: ", err);
    //             DeletionToaster.show({ message: `Failed To Delete Template: ${selectedTemplate?.name}`, intent: Intent.DANGER });
    //         });
    // };
    return (
        <div id="template-grid">
            {error && (
                <NonIdealState
                    icon={"error"}
                    title="Templates Not Available"
                    description="Failed to fetch available templates. Please contact the administrator."
                />
            )}
            <div className={`ag-theme-balham${dark ? "-dark" : " "} ag`}>
                <ShowDeleteAlertContext.Provider value={[confirmOpen, setConfirmOpen]}>
                    <TemplateGridSelectContext.Provider value={[selectedTemplate, setSelectedTemplate]}>
                        <AgGridReact modules={AllModules} gridOptions={gridOptions} onRowClicked={onRowClicked} />
                        <CreateInstructionRunDiaglog
                            template={selectedTemplate!}
                            isOpen={dialogOpen}
                            setIsOpen={setDialogOpen}
                        />
                    </TemplateGridSelectContext.Provider>
                </ShowDeleteAlertContext.Provider>
            </div>
        </div>
    );
}

function reshape(data: GetAvailableTemplates | undefined) {
    if (!data || !data.availableProcessTemplates) {
        return [];
    }
    const companies = data.companies;
    const getName = (id: string | undefined) => companies.find(c => c.id === id);
    return data.availableProcessTemplates.map(t => {
        return {
            ...t,
            id: t?.processId,
            company: getName(t?.companySysId)?.name,
            name: t?.name,
            description: t?.description
        };
    });
}
const columnDefs: ColDef[] = [
    {
        colId: "processId",
        headerName: "Id",
        field: "processId",
        cellClass: "no-border",
        hide: true
    },
    {
        colId: "company",
        headerName: "Company",
        field: "company",
        cellClass: "no-border",
        pinned: "left"
    },
    {
        colId: "name",
        headerName: "Name",
        field: "name",
        cellClass: "no-border"
        // cellRenderer: "companyCellRenderer"
    },
    // {
    //     colId: "updateOn",
    //     headerName: "Last Updated",
    //     field: "updatedOn",
    //     valueGetter: ({ data }) => new Date(data.updatedOn),
    //     valueFormatter: ({ data }) => relativeTime(data.updatedOn),
    //     tooltipValueGetter: ({ value }) => localTime(value),
    //     cellClass: "no-border",
    //     filter: ""
    // },
    // {
    //     colId: "createdOn",
    //     headerName: "Created On",
    //     field: "createdOn",
    //     valueFormatter: ({ data }) => localTime(data.createdOn),
    //     cellClass: "no-border"
    // },
    {
        colId: "description",
        headerName: "Description",
        field: "description",
        cellClass: "no-border",
        flex: 1
    }
];
